<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/interactions';
import typesDocs from 'docs:@react-types/shared/src/events.d.ts';
import {HeaderInfo, FunctionAPI, TypeContext, InterfaceType, TypeLink} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {useKeyboard} from '@react-aria/interactions';
```

---
category: Interactions
---

# useKeyboard

<p>{docs.exports.useKeyboard.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useKeyboard']} />

## API

<FunctionAPI function={docs.exports.useKeyboard} links={docs.links} />

## Features

`useKeyboard` handles keyboard interactions. The only difference from DOM events is that propagation
is stopped by default if there is an event handler, unless `event.continuePropagation()` is called.
This provides better modularity by default, so that a parent component doesn't respond to an event
that a child already handled. If the child doesn't handle the event (e.g. it was for an unknown key),
it can call `event.continuePropagation()` to allow parents to handle the event.

## Usage

`useKeyboard` returns props that you should spread onto the target element:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useKeyboard.return.id].properties} />
</TypeContext.Provider>

`useKeyboard` supports the following event handlers:

<TypeContext.Provider value={typesDocs.links}>
  <InterfaceType properties={typesDocs.exports.KeyboardEvents.properties} />
</TypeContext.Provider>

## Example

This example shows a simple input element that handles keyboard events with `useKeyboard` and logs them to a list below.

**NOTE: for more advanced text field functionality, see [useTextField](useTextField.html).**

```tsx example
function Example() {
  let [events, setEvents] = React.useState([]);
  let {keyboardProps} = useKeyboard({
    onKeyDown: e => setEvents(
      events => [`key down: ${e.key}`, ...events]
    ),
    onKeyDown: e => setEvents(
      events => [`key up: ${e.key}`, ...events]
    )
  });

  return (
    <>
      <input 
        {...keyboardProps}
        aria-label="Example"
        placeholder="Type here" />
      <ul style={{
        height: 100,
        overflow: 'auto',
        border: '1px solid gray',
        width: 200
      }}>
        {events.map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    </>
  );
}
```
